import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useLocation, Link } from "react-router-dom";
import { MessageCircle, X, Send, User, Loader2 } from "lucide-react";
import { getAuthHeaders } from "../context/AuthContext";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useCountry } from "../context/CountryContext";
import { Sparkles, FileText } from "lucide-react";
import { AIResultCard } from "./AIResultCard";
import CVPdfViewerModal from "./CVPdfViewerModal";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single official visual identity for InfoCity IA — same face for guest/candidate/employer.
// Only the role's copy/behavior changes, never the avatar (per sprint scope).
const AVATAR_URL = "/ai/infocity-ia-avatar.png";

const GENERAL_SUGGESTIONS = ["Find tech jobs", "How to apply?", "Resume tips"];

// Maps the current route (+ tab query param) to a short context code the
// backend uses to PRIORITIZE help within the user's role — it never changes
// the role itself. Kept intentionally simple: unknown routes → null (no hint).
function getPageContext(pathname, search) {
  const tab = new URLSearchParams(search).get("tab");
  if (pathname === "/") return "home";
  if (pathname === "/jobs") return "job_search";
  if (pathname.startsWith("/jobs/")) return "job_detail";
  if (pathname === "/cv-builder") return "cv_builder";
  if (pathname === "/messages") return "messages";
  if (pathname === "/dashboard") {
    if (tab === "cv") return "cv_builder";
    if (tab === "myjobs" || tab === "saved") return "candidate_myjobs";
    if (tab === "myprofile" || tab === "profile" || tab === "settings") return "candidate_profile";
    if (tab === "whoviewedme") return "candidate_whoviewedme";
    return "candidate_applications";
  }
  if (pathname === "/company/dashboard") {
    if (tab === "post") return "employer_post_job";
    if (tab === "jobs") return "employer_jobs";
    if (tab === "applicants") return "employer_candidates";
    if (tab === "company") return "employer_company";
    return "employer_overview";
  }
  return null;
}

// Role is derived ONLY from the real auth session, never from the URL.
function getRole(user) {
  if (user?.role === "candidate") return "candidate";
  if (user?.role === "employer") return "employer";
  return "guest";
}

export default function AIChat() {
  const { user } = useAuth();
  const { t, tList, language } = useLanguage();
  const { country } = useCountry();
  const location = useLocation();
  const role = getRole(user);
  const roleCap = role.charAt(0).toUpperCase() + role.slice(1);
  // Shift the FAB/panel up on mobile so it never overlaps the global,
  // unified BottomNav — visible on every route now except /cv-builder (its
  // own fixed Anterior/Siguiente/Guardar bar). BottomNav IS shown on
  // /company-view too, so this must not exclude it.
  const bottomNavVisible = location.pathname !== "/cv-builder";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // Hides while Home's mobile filters bottom sheet is open (custom event —
  // same lightweight pattern as useSplashHidden), so the FAB never floats
  // above the sheet's own controls. Never affects any other AIChat logic.
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  useEffect(() => {
    const onFiltersSheet = (e) => setFiltersSheetOpen(!!e.detail?.open);
    window.addEventListener("infocity:filters-sheet", onFiltersSheet);
    return () => window.removeEventListener("infocity:filters-sheet", onFiltersSheet);
  }, []);
  const [sessionId, setSessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  // Structured `action` from the backend (e.g. open_cv) — only rendered as a
  // real button when the backend actually returned one, never inferred here.
  const [viewingCv, setViewingCv] = useState(null);
  const bottomRef = useRef(null);
  // Auto-hide the floating button while the user is actively scrolling down a
  // page (it inevitably sits above some content at rest) — reveals again as
  // soon as they pause or scroll back up, so it never permanently blocks content.
  const [fabVisible, setFabVisible] = useState(true);
  const lastScrollY = useRef(typeof window !== "undefined" ? window.scrollY : 0);
  useEffect(() => {
    let hideTimer;
    const onScroll = () => {
      const y = window.scrollY;
      const goingDown = y > lastScrollY.current + 4;
      const goingUp = y < lastScrollY.current - 4;
      if (goingDown && y > 80) setFabVisible(false);
      else if (goingUp || y <= 80) setFabVisible(true);
      lastScrollY.current = y;
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setFabVisible(true), 900);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); clearTimeout(hideTimer); };
  }, []);

  // Role changed (login/logout/switch account) → start a brand-new session so
  // the LLM never carries context across roles/users, and re-greet for the
  // new role. This is what makes "candidate → logout → guest" instant.
  useEffect(() => {
    setSessionId(`session_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    const firstName = user?.name?.split(" ")[0];
    const greetingKey = role === "candidate" ? (firstName ? "ai.greetingCandidate" : "ai.greetingCandidateGeneric")
      : role === "employer" ? (firstName ? "ai.greetingEmployer" : "ai.greetingEmployerGeneric")
      : "ai.greetingGuest";
    setMessages([{ role: "assistant", content: t(greetingKey).replace("{name}", firstName || "") }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, user?.user_id, t]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    const newMessages = [...(messages || []), { role: "user", content: msg }];
    setMessages(newMessages);
    setLoading(true);
    try {
      const page_context = getPageContext(location.pathname, location.search);
      const res = await axios.post(
        `${API}/ai/chat`,
        { message: msg, session_id: sessionId, page_context, language, country_code: country },
        { headers: getAuthHeaders() }
      );
      setMessages([...newMessages, { role: "assistant", content: res.data.response, results: res.data.results || null, viewAll: res.data.view_all || null, action: res.data.action || null }]);
    } catch {
      setMessages([...newMessages, { role: "assistant", content: t("ai.error") }]);
    } finally {
      setLoading(false);
    }
  };

  const subtitle = t(`ai.subtitle${roleCap}`);
  const placeholder = t(`ai.placeholder${roleCap}`);
  const suggestions = tList(`ai.suggestions${roleCap}`, GENERAL_SUGGESTIONS);
  const isFirstMsg = (messages || []).length <= 1;

  return (
    <>
      {/* Chat Panel */}
      {open && !viewingCv && (
        <div
          data-testid="ai-chat-panel"
          className={bottomNavVisible ? "ai-chat-panel-shift" : "ai-chat-panel"}
          style={{
            position: "fixed", right: "calc(16px + env(safe-area-inset-right))", zIndex: 80,
            width: "360px", maxWidth: "calc(100vw - 32px)",
            background: "white", borderRadius: "20px",
            boxShadow: "0 12px 28px -6px rgba(15,23,42,0.22), 0 4px 12px rgba(15,23,42,0.08)",
            border: "1px solid #E5E7EB", display: "flex", flexDirection: "column",
            maxHeight: "560px", overflow: "hidden",
          }}
        >
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px",
            background: "linear-gradient(135deg, #F55FA5, #C94E87)",
            color: "white",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }} data-testid="ai-chat-role-badge" data-role={role}>
              <img src={AVATAR_URL} alt="InfoCity IA" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.5)", flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: "15px", fontWeight: 700, fontFamily: "Poppins, sans-serif", lineHeight: 1.2 }}>
                  {t("ai.title")}
                </p>
                <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.75)", fontFamily: "Poppins, sans-serif" }} data-testid="ai-chat-subtitle">
                  {subtitle}
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{ width: 28, height: 28, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer", color: "white" }} data-testid="close-chat">
              <X style={{ width: 14, height: 14 }} />
            </button>
          </div>

          {/* Role context hint */}
          {role !== "guest" && (
            <div style={{ padding: "8px 12px", background: "rgba(245,95,165,0.06)", borderBottom: "1px solid rgba(245,95,165,0.15)", display: "flex", alignItems: "center", gap: "6px" }}>
              <Sparkles style={{ width: 12, height: 12, color: "#F55FA5" }} />
              <span style={{ fontSize: "11px", color: "#64748B", fontFamily: "Poppins, sans-serif" }}>
                {role === "candidate" ? t("ai.usingCvHint") : t("ai.usingCompanyHint")}
              </span>
            </div>
          )}

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "14px", background: "#F8FAFC", minHeight: 0, maxHeight: "280px" }}>
            {(messages || []).map((msg, i) => (
              <div key={i} data-testid={`chat-message-${i}`}
                style={{ display: "flex", gap: "8px", marginBottom: "12px", flexDirection: msg.role === "user" ? "row-reverse" : "row" }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, marginTop: "2px", overflow: "hidden", background: msg.role === "user" ? "#028CF8" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {msg.role === "assistant"
                    ? <img src={AVATAR_URL} alt="InfoCity IA" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <User style={{ width: 12, height: 12, color: "white" }} />
                  }
                </div>
                <div style={{
                  maxWidth: "82%", padding: "10px 12px", borderRadius: msg.role === "user" ? "14px 4px 14px 14px" : "4px 14px 14px 14px",
                  fontSize: "14px", lineHeight: "1.5", fontFamily: "Poppins, sans-serif", whiteSpace: "pre-wrap",
                  background: msg.role === "user" ? "#028CF8" : "white",
                  color: msg.role === "user" ? "white" : "#374151",
                  border: msg.role === "assistant" ? "1px solid #E5E7EB" : "none",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}>
                  {msg.content}
                  {Array.isArray(msg.results) && msg.results.length > 0 && (
                    <div data-testid={`chat-results-${i}`}>
                      {msg.results.map((r, ri) => <AIResultCard key={ri} result={r} t={t} />)}
                    </div>
                  )}
                  {msg.viewAll && (
                    <Link to={msg.viewAll.url} data-testid={`chat-view-all-${i}`}
                      style={{ display: "inline-block", marginTop: "8px", fontSize: "12px", fontWeight: 600, color: "#028CF8", textDecoration: "underline" }}>
                      {msg.viewAll.label}
                    </Link>
                  )}
                  {msg.action?.type === "open_cv" && (
                    <button
                      data-testid={`chat-action-open-cv-${i}`}
                      onClick={() => setViewingCv({ application_id: msg.action.application_id, cv_type: msg.action.cv_type, name: msg.action.candidate_name, job_title: msg.action.job_title })}
                      style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px", fontSize: "12px", fontWeight: 600, padding: "6px 10px", borderRadius: "8px", border: "none", background: "#028CF8", color: "white", cursor: "pointer" }}
                    >
                      <FileText style={{ width: 12, height: 12 }} />
                      {msg.action.label}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
                  <img src={AVATAR_URL} alt="InfoCity IA" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ padding: "10px 12px", background: "white", border: "1px solid #E5E7EB", borderRadius: "4px 14px 14px 14px" }}>
                  <Loader2 style={{ width: 16, height: 16, color: "#F55FA5", animation: "spin 1s linear infinite" }} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick suggestions */}
          {isFirstMsg && (
            <div style={{ padding: "8px 12px", background: "#F8FAFC", display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {(Array.isArray(suggestions) ? suggestions : GENERAL_SUGGESTIONS).map((s, i) => (
                <button key={i} data-testid={`chat-suggestion-${i}`} onClick={() => sendMessage(s)}
                  style={{ fontSize: "12.5px", padding: "5px 11px", borderRadius: "999px", background: "white", border: "1px solid #E5E7EB", color: "#64748B", cursor: "pointer", fontFamily: "Poppins, sans-serif", fontWeight: 500 }}>
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            style={{ padding: "10px 12px", borderTop: "1px solid #F1F5F9", background: "white", display: "flex", gap: "8px" }}>
            <input
              data-testid="chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              style={{ flex: 1, minWidth: 0, fontSize: "16px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "8px 12px", fontFamily: "Poppins, sans-serif", outline: "none", color: "#0F172A" }}
              disabled={loading}
            />
            <button type="submit" data-testid="send-chat-button" disabled={loading || !input.trim()}
              style={{ width: 36, height: 36, background: "#F55FA5", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", color: "white", border: "none", cursor: "pointer", opacity: loading || !input.trim() ? 0.5 : 1, flexShrink: 0 }}>
              <Send style={{ width: 15, height: 15 }} />
            </button>
          </form>
        </div>
      )}

      {/* Floating Button — InfoCity IA brand pink (#F55FA5); this is the ONE
          deliberate exception to "blue = primary CTA" since AI is the brand
          differentiator. Fades/shrinks while scrolling down so it never
          permanently sits over content; reappears on scroll-up/pause/open. */}
      {!viewingCv && !filtersSheetOpen && (
        <button
          data-testid="ai-chat-button"
          onClick={() => { if (!open) track("ai_opened"); setOpen(!open); }}
          style={{
            position: "fixed", right: "calc(16px + env(safe-area-inset-right))", zIndex: 80,
            width: 48, height: 48, borderRadius: "50%",
            background: open ? "#64748B" : "white",
            border: open ? "none" : "2.5px solid #F55FA5",
            padding: 0, overflow: "hidden",
            color: "white", cursor: "pointer",
            boxShadow: "0 4px 14px rgba(15,23,42,0.28)",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s ease",
            opacity: (open || fabVisible) ? 1 : 0,
            transform: (open || fabVisible) ? "scale(1)" : "scale(0.6)",
            pointerEvents: (open || fabVisible) ? "auto" : "none",
          }}
          className={`hover:scale-110 ${bottomNavVisible ? "ai-chat-fab-shift" : "ai-chat-fab"}`}
        >
          {open
            ? <X style={{ width: 22, height: 22 }} />
            : <img src={AVATAR_URL} alt="InfoCity IA" style={{ width: "100%", height: "100%", objectFit: "cover", aspectRatio: "1 / 1" }} />
          }
        </button>
      )}
      {viewingCv && (
        <CVPdfViewerModal
          title={viewingCv.name}
          subtitle={viewingCv.job_title}
          fetchPath={viewingCv.cv_type ? `/applications/${viewingCv.application_id}/cv-pdf` : `/applications/${viewingCv.application_id}/cv`}
          downloadFilename={`${(viewingCv.name || "CV").replace(/\s+/g, "_")}_CV.pdf`}
          onClose={() => setViewingCv(null)}
        />
      )}
    </>
  );
}
